/**
 * ChatOrderItemDetails — renders bundle contents and personalization info
 * below each cart item in the ChatOrderSheet order summary (Step 2).
 *
 * Mirrors the storefront's order-items-section.tsx pattern:
 * - Bundle items: bordered card listing component names + selected variant attributes
 * - Personalized items: label/value pairs for customization fields
 */

import { Package } from 'lucide-react';

interface ChatOrderItemDetailsProps {
  itemCode: string;
  /** Bundle selections: { component_code: { attribute: value } } */
  bundleSelections?: Record<string, Record<string, string>>;
  /** Personalization: { field_label: value } */
  customizations?: Record<string, string>;
  /** Units this personalization covers — shown when one summary row merges several lines */
  unitQty?: number;
}

/** Strip prefix (shared:Label or ITEM_CODE:Label → Label) */
function fieldLabel(rawLabel: string): string {
  return rawLabel.includes(':') ? rawLabel.split(':').slice(1).join(':') : rawLabel;
}

/** One-line personalization summary for compact cart rows ("Label: value · …"). */
export function personalizationNote(customizations?: Record<string, string>): string | undefined {
  if (!customizations) return undefined;
  const parts = Object.entries(customizations)
    .filter(([, value]) => value)
    .map(([rawLabel, value]) => `${fieldLabel(rawLabel)}: ${value}`);
  return parts.length ? parts.join(' · ') : undefined;
}

export function ChatOrderItemDetails({
  bundleSelections,
  customizations,
  unitQty,
}: ChatOrderItemDetailsProps) {
  const hasBundleData = bundleSelections && Object.keys(bundleSelections).length > 0;
  const hasCustomizations = customizations && Object.keys(customizations).length > 0;

  if (!hasBundleData && !hasCustomizations) return null;

  return (
    <div className="mt-1 space-y-1.5 pl-2">
      {/* Bundle contents */}
      {hasBundleData && (
        <div className="rounded border border-border/60 bg-background/50 px-2 py-1.5">
          <p className="mb-1 flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
            <Package className="h-3 w-3" />
            Bundle Contents
          </p>
          <div className="space-y-0.5">
            {Object.entries(bundleSelections!).map(([componentCode, attrs]) => {
              const variantLabel = Object.values(attrs).join(' / ');
              return (
                <div key={componentCode} className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <span className="h-1 w-1 shrink-0 rounded-full bg-muted-foreground/40" />
                  <span className="truncate">
                    {componentCode}
                    {variantLabel && (
                      <span className="ml-1 text-[11px] text-muted-foreground/70">
                        ({variantLabel})
                      </span>
                    )}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Personalization fields */}
      {hasCustomizations && (
        <div className="rounded border border-border/60 bg-background/50 px-2 py-1.5">
          <p className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
            Personalization
            {unitQty !== undefined && (
              <span className="ml-1 normal-case tracking-normal">× {unitQty}</span>
            )}
          </p>
          <div className="space-y-0.5">
            {Object.entries(customizations!).map(([rawLabel, value]) => {
              const label = fieldLabel(rawLabel);
              return (
                <div key={rawLabel} className="flex items-baseline gap-1.5 text-xs">
                  <span className="shrink-0 text-muted-foreground">{label}:</span>
                  <span className="truncate text-foreground">{value}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
